import React, { useState, useEffect, useRef } from "react";
import { TextField, CircularProgress, Box } from "@mui/material";
import vendorService from "@/modules/vendor/service/vendor.int.service";

const GooglePlacesInput = ({ value, onChange, onAddressSelect }) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [placePredictions, setPlacePredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const debounceTimeout = useRef(null); // Reference for debounce timeout
  const abortController = useRef(new AbortController()); // Reference for abort controller

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleInputChange = async (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    onChange(newValue);

    // Clear the previous timeout if it exists
    clearTimeout(debounceTimeout.current);

    // Set a new timeout to call the API after 2 seconds of inactivity
    debounceTimeout.current = setTimeout(async () => {
      if (newValue) {
        // Cancel the previous request if it exists
        abortController.current.abort();

        // Create a new AbortController for the new request
        abortController.current = new AbortController();

        setIsMenuVisible(true);
        setIsLoading(true);
        try {
          // Make the API call and pass the signal to handle cancellation
          const response = await vendorService.getPlacesAutocomplete(newValue, {
            signal: abortController.current.signal,
          });
          setPlacePredictions(response.predictions);
        } catch (error) {
          if (error.name !== "AbortError") {
            console.error("Error fetching place predictions:", error);
          }
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsMenuVisible(false);
      }
    }, 500); // 2-second debounce time
  };

  const handleSelect = async (place) => {
    try {
      const response = await vendorService.getPlaceDetails(place.place_id);
      const placeDetails = response.result;
      if (placeDetails) {
        const location = placeDetails.geometry.location;
        const postalCode = placeDetails.address_components.find(component => component.types.includes('postal_code'));

        setInputValue(placeDetails.formatted_address);
        setIsMenuVisible(false);
        onChange(placeDetails.formatted_address);
        onAddressSelect({
          address: placeDetails.formatted_address,
          postalCode: postalCode?.long_name,
          latitude: location.lat,
          longitude: location.lng,
        });
      }
    } catch (error) {
      console.error("Error fetching place details:", error);
    }
  };

  return (
    <Box position="relative">
      <TextField
        fullWidth
        value={inputValue}
        onChange={handleInputChange}
        placeholder="Search for a location"
        variant="outlined"
      />
      {isLoading && <CircularProgress size={24} sx={{ position: "absolute", top: 10, right: 12 }} />}
      {isMenuVisible && (
        <Box
          sx={{
            position: "absolute",
            backgroundColor: "#fff",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            marginTop: "8px",
            maxHeight: "200px",
            overflowY: "auto",
            width: "100%",
            borderRadius: "8px",
          }}
        >
          {placePredictions.map((place) => (
            <Box
              key={place.place_id}
              sx={{
                padding: "8px 12px",
                cursor: "pointer",
                backgroundColor: "#f0f0f0",
                borderRadius: "8px",
                transition: "background-color 0.2s ease",
              }}
              onClick={() => handleSelect(place)}
            >
              {place.description}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default GooglePlacesInput;
